import React, { useState, useEffect, useRef } from 'react'
import { Dialog, TextInput, Label, Button, Text, Flex } from '@leverege/ui-elements'

import S from './EnumEditorStyle.css'

const initialState = {
  type : { value : '', error : null },
  name : { value : '', error : null },
  description : { value : '', error : null },
}

/**
 * Dialog for creating a new enum type
 * @param {boolean} show does the dialog show
 * @param {function} onClose
 * @param {function} onSubmit
 * @param {any} eventData returned with onSubmit and onClose
 */
export default function EnumTypeCreateDialog( { show, onClose, eventData, onSubmit } ) {
  const [ formData, setFormData ] = useState( initialState )

  const dataRef = useRef( formData )
  useEffect( () => { dataRef.current = formData }, [ formData ] )

  const handleSubmit = () => {
    const value = Object.entries( dataRef.current ).reduce( ( acc, cur ) => ( {
      ...acc,
      [cur[0]] : cur[1].value.trim()
    } ), {} )
    value.name = value.name || value.type // use type as default name
    onSubmit( { data : eventData, value } )
    setFormData( initialState )
    onClose( { data : eventData } )
  }

  /**
   * Allow submit by pressing return
   */
  useEffect( () => {
    const submitOnReturn = ( e ) => {
      if ( ( e.which === 13 || e.keyCode === 13 ) && dataRef.current?.type?.value ) {
        handleSubmit()
      }
    }
    if ( show ) {
      document.addEventListener( 'keyup', submitOnReturn )
    } else {
      document.removeEventListener( 'keyup', submitOnReturn )
    }
    return () => document.removeEventListener( 'keyup', submitOnReturn )
  }, [ show ] )

  const onChange = ( { data, value } ) => {
    const nData = { ...formData,
                    [data] : {
                      value,
                      error : ( data !== 'description' && !value ) ? 'Required' : null } }
    setFormData( nData )
  }

  const { type, description, name } = formData
  return (
    <Dialog
      variant="dialogMedium"
      show={show}
      onClose={onClose}
      footer={(
        <Flex variant="dialogButtons">
          <Button onClick={onClose} eventData={eventData} variant="secondary">Cancel</Button>
          <Button
            onClick={handleSubmit}
            eventData={eventData}
            disabled={!type.value}>Create
          </Button>
        </Flex>
      )}
      title="Create New Enum Type">
      <div className={S.inputGroup}>
        <Label variant="input">Type (required)</Label>
        <TextInput
          acceptKey={isAlphaNumeric}
          hint="e.g., assetType, vehicleColor"
          eventData="type"
          alwaysFireOnReturn={false}
          changeOnReturn={false}
          validator={() => !type.error}
          value={type.value}
          onChange={onChange}
          style={{ width : '100%', maxWidth : '250px' }} />
        <Text variant={type.error ? 'inputCaptionSmallError' : 'caption'}>
          {type.error || 'Enum key used in code -- no spaces or special characters'}
        </Text>
      </div>
      <div className={S.inputGroup}>
        <Label variant="input">Name</Label>
        <TextInput
          hint="e.g., Asset Type, Vehicle Color"
          eventData="name"
          value={name.value}
          onChange={onChange}
          style={{ width : '100%', maxWidth : '250px' }} />
      </div>
      <div className={S.inputGroup}>
        <Label variant="input">Description</Label>
        <TextInput
          hint="A brief description of what this enum represents"
          variant="inputGrow"
          eventData="description"
          value={description.value}
          onChange={onChange}
          style={{ width : '100%', maxWidth : '500px' }} />
      </div>
    </Dialog>
  )
}

function isAlphaNumeric( keyPressEvent ) {
  const code = keyPressEvent.charCode
  return ( code > 47 && code < 58 ) || // 0-9
    ( code > 64 && code < 91 ) || // A-Z
    ( code > 96 && code < 123 ) // a-z
}
